import React, { useEffect, useRef, useState } from "react";
import { Building2, Handshake, Award } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const ChairmanMessage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [content, setContent] = useState<any>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t_api } = useLanguage();

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => setContent(data.Chairman || {}))
      .catch(err => console.warn('Chairman content unavailable:', err));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => setIsVisible(e.isIntersecting)),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.unobserve(sectionRef.current); };
  }, []);

  const points = [
    { title: t_api(content?.point_1_title || "Our Heritage", content?.point_1_title_ar), desc: t_api(content?.point_1_subtitle || "Decades of excellence since 1975", content?.point_1_subtitle_ar), icon: Building2 },
    { title: t_api(content?.point_2_title || "Our Values", content?.point_2_title_ar), desc: t_api(content?.point_2_subtitle || "Integrity and quality approach", content?.point_2_subtitle_ar), icon: Handshake },
    { title: t_api(content?.point_3_title || "Quality Policy", content?.point_3_title_ar), desc: t_api(content?.point_3_subtitle || "Commitment to excellence", content?.point_3_subtitle_ar), icon: Award },
  ];

  return (
    <div ref={sectionRef} className="bg-gray-50 px-2 md:px-0 pb-8 pt-8">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Left Side: Message */}
          <div className={`flex-1 transition-all duration-1000 ${isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}`}>
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-800">{t_api(content?.title || "Founder's Message", content?.title_ar)}</h1>
              <div className="w-16 h-1 bg-[#F2BB13] mt-2" />
            </div>
            <div className="text-gray-600 leading-relaxed space-y-3 text-justify">
              {t_api(content?.desc || "Welcome To Hamdan Trading Group...", content?.desc_ar).split('\n').filter(Boolean).map((para: string, i: number) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: para }} />
              ))}
              <div className="pt-4">
                <p className="font-medium mb-3">{t_api("Sincerely,")}</p>
                {content?.image_1 && (
                  <div className="mb-4">
                    <img 
                      src={content.image_1.startsWith('http') ? content.image_1 : (content.image_1.startsWith('/') ? content.image_1 : '/' + content.image_1)} 
                      alt="Founder Signature / Image" 
                      className="max-h-48 w-auto object-contain rounded-xl shadow-md border border-slate-100 bg-white p-2" 
                    />
                  </div>
                )}
                <p className="font-bold text-lg text-gray-900">{t_api(content?.sig_name || "Hamed bin Hamood Al Ghafri", content?.sig_name_ar)}</p>
                <p className="text-[#F2BB13] font-semibold text-sm">{t_api(content?.sig_role || "Founder & Chairman", content?.sig_role_ar)}</p>
              </div>
            </div>
          </div>

          {/* Right Side: Feature Boxes */}
          <div className={`lg:w-96 w-full transition-all duration-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <div className="flex flex-col gap-4">
              {points.map((p, i) => (
                <div key={i} className="bg-white p-7 rounded-2xl shadow-md text-center group w-full border border-gray-50 flex flex-col items-center justify-center min-h-[160px]">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-4 group-hover:bg-red-100 transform group-hover:scale-110 transition-all">
                    <p.icon size={28} className="text-red-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{p.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChairmanMessage;
